import { Component, inject } from '@angular/core';
import { ComponentsModule, DialogService, SnackBarService } from '@mastechnicaegroup/carelinelive-ui';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Client, createClientDetailsFormGroup, ClientSingleResponse, StoreClientRequest } from '@lib/api/models/client';
import { createPersonDetailsFormGroup } from '@lib/api/models/person';
import { createAddressFormGroup } from '@lib/api/models/address';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SendableRequest } from '@mastechnicaegroup/carelinelive-cdk/requests';
import { ClientService } from '@lib/client/client.service';
import { NgForOf } from '@angular/common';
import { createContactNumberFormGroup, StoreContactNumberRequest } from '@lib/api/models/contact-numbers';

@Component({
    selector: 'app-client-contact-address-dialog',
    imports: [
        ComponentsModule,
        ErrorPanelComponent,
        FormsModule,
        ReactiveFormsModule,
        NgForOf
    ],
    templateUrl: './edit-client-contact-address-dialog.component.html',
    standalone: true,
    styleUrl: './edit-client-contact-address-dialog.component.scss'
})
export class EditClientContactAddressDialogComponent {

    private readonly _clientService = inject(ClientService);
    private readonly _snackBar = inject(SnackBarService);

    protected readonly client = inject<Client>(DIALOG_DATA);
    protected readonly clientDetailsFormGroup = createClientDetailsFormGroup(this.client);
    protected readonly personDetailsFormGroup = createPersonDetailsFormGroup(this.client.person);
    protected readonly addressFormGroup = createAddressFormGroup(this.client.person.address);
    protected readonly dialogRef = inject<DialogRef>(DialogRef);

    contactNumbersForm: FormGroup;

    updateClientContactNumberRequest = new SendableRequest<ClientSingleResponse, StoreClientRequest>({
        request: payload => this._clientService.updateClient(this.client.id, payload!),

        onSuccess: () => {
            return;
        }
    });

    updateClientRequest = new SendableRequest<ClientSingleResponse, StoreClientRequest>({
        request: (payload) => this._clientService.updateClient(this.client.id, payload!),

        onSend: () => {
            this.addressFormGroup.disable();
        },

        onSuccess: response => {
            this.addressFormGroup.enable();

            let contactNumbers = this.contactNumbersForm.value.contactNumbers;

            if (contactNumbers.length === 1) {
                if (contactNumbers[0].number === '' && contactNumbers[0].type === null) {
                    contactNumbers = [];
                }
            }

            this.addContactNumberToClient(response.data, contactNumbers);

            this._snackBar.open({
                colour: 'success',
                icon: 'check',
                title: 'Contact & Property Updated'
            });
            this.dialogRef.close();
        },

        onError: () => {
            this.addressFormGroup.enable();
        }
    });

    constructor(private fb: FormBuilder) {
        this.contactNumbersForm = this.fb.group({
            contactNumbers: this.fb.array(
                this.client.contact_numbers.map((contact) => createContactNumberFormGroup(contact))
            )
        });
    }

    get contactNumbersFormArray(): FormArray {
        return this.contactNumbersForm.get('contactNumbers') as FormArray;
    }

    addContactNumberToClient(client: Client, contactNumbers: StoreContactNumberRequest[]) {
        const clientData: StoreClientRequest = {
            reference: client.reference,
            nhs_number: client.nhs_number,
            email: client.email,
            person: {
                title: client.person.title,
                given_name: client.person.given_name,
                initials: client.person.initials,
                family_name: client.person.family_name,
                middle_names: client.person.middle_names,
                preferred_name: client.person.preferred_name,
                birth_sex: client.person.birth_sex,
                pronouns: client.person.pronouns,
                date_of_birth: client.person.date_of_birth,
                additional_comments: client.person.additional_comments,
            },
            contact_numbers_to_store: contactNumbers
        };

        this.updateClientContactNumberRequest.send(clientData);
    }

    addContactNumber() {
        this.contactNumbersFormArray.push(createContactNumberFormGroup());
    }

    removeContactNumber(index: number) {
        this.contactNumbersFormArray.removeAt(index);

        if (this.contactNumbersFormArray.length === 0) {
            this.addContactNumber();
        }
    }

    update() {
        if (this.updateClientRequest.loading()) {
            return;
        }

        const clientData: StoreClientRequest = {
            reference: this.clientDetailsFormGroup.get('reference')?.value ?? this.client.reference,
            nhs_number: this.clientDetailsFormGroup.get('nhs_number')?.value ?? this.client.nhs_number,
            email: this.clientDetailsFormGroup.get('email')?.value ?? '',
            person: {
                title: this.client.person.title,
                given_name: this.client.person.given_name,
                initials: this.client.person.initials,
                family_name: this.client.person.family_name,
                middle_names: this.client.person.middle_names,
                preferred_name: this.client.person.preferred_name,
                birth_sex: this.client.person.birth_sex,
                pronouns: this.client.person.pronouns,
                date_of_birth: this.client.person.date_of_birth,
                additional_comments: this.client.person.additional_comments,
                address: {
                    'street_1': this.addressFormGroup.get('street_1')?.value ?? '',
                    'street_2': this.addressFormGroup.get('street_2')?.value ?? '',
                    'town': this.addressFormGroup.get('town')?.value ?? '',
                    'county': this.addressFormGroup.get('county')?.value ?? '',
                    'country': this.addressFormGroup.get('country')?.value ?? '',
                    'post_code': this.addressFormGroup.get('post_code')?.value ?? '',
                    'hard_to_reach': this.addressFormGroup.get('hard_to_reach')?.value ?? false,
                    'lat': this.addressFormGroup.get('lat')?.value ?? 0,
                    'lng': this.addressFormGroup.get('lng')?.value ?? 0,
                    'additional_comments': this.addressFormGroup.get('additional_comments')?.value ?? ''
                }
            }
        };

        this.updateClientRequest.send(clientData);
    }

    public static show(dialog: DialogService, data: Client) {
        return dialog.openDialog<DialogRef, Client>(EditClientContactAddressDialogComponent, {
            data
        });
    }
}
